import {
  CompanyProductSet,
  CompanyProductSetProduct,
  RevenueShareClass,
} from '@upright/common/model/input/companies/products/types';
import { getDuplicates } from '@upright/common/utils/get-duplicates';
import { safeAssert as assert } from '@upright/common/safe-assert';
import { isNil } from 'lodash';

export function assertNoDuplicateProducts(products: CompanyProductSetProduct[]) {
  const productCodes = products.map(product => product.productCode);
  const duplicatedProducts = getDuplicates(productCodes);
  if (duplicatedProducts.length) {
    throw Error(`Duplicated product codes: ${duplicatedProducts.join(', ')}`);
  }
}

/* NOTE: this is function is not unit tested by itself, but withReplacedProducts is built around this and has a decent test coverage */
export function mergeProducts(
  productA: CompanyProductSetProduct | undefined,
  productB: CompanyProductSetProduct | undefined,
): CompanyProductSetProduct | undefined {
  if (!productA || !productB) {
    return productA ?? productB;
  }
  assert(productA.productCode === productB.productCode);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  assert(productA.version === productB.version);

  const revenueShare = sumValues(productA.revenueShare, productB.revenueShare);
  const revenueShareClass = isNil(revenueShare)
    ? higherClass(productA.revenueShareClass, productB.revenueShareClass)
    : undefined;

  return {
    productCode: productA.productCode,
    version: productA.version,
    revenueShareClass,
    revenueShare,
  };
}

function sumValues(a: number | undefined, b: number | undefined): number | undefined {
  return !isNil(a) && !isNil(b) ? a + b : (a ?? b);
}

function higherClass(
  classA: RevenueShareClass | undefined,
  classB: RevenueShareClass | undefined,
): RevenueShareClass | undefined {
  const { MAJOR, MEDIUM, MINOR, UNKNOWN } = RevenueShareClass;
  const hasClass = (_class: RevenueShareClass | undefined) => [classA, classB].includes(_class);
  return hasClass(MAJOR)
    ? MAJOR
    : hasClass(MEDIUM)
      ? MEDIUM
      : hasClass(MINOR)
        ? MINOR
        : hasClass(UNKNOWN)
          ? UNKNOWN
          : undefined;
}

export function isEditableCompanyProductSet(productSet: CompanyProductSet) {
  if (isResolvedSet(productSet)) {
    return false;
  }
  // the editing time frame limit does not make sense for PLG companies
  // (not sure whether it makes sense anyway, but limiting scope of change)
  // -- JO 2025-10-17
  if (productSet.source === 'manual-plg-piu') {
    return true;
  }
  return isWithinAllowedProductSetEditingTimeframe(productSet);
}

// Product set editing is allowed for two weeks since creation.
export const productSetEditingTimeFrameDays = 14;
export const productSetEditingTimeFrameMs = productSetEditingTimeFrameDays * 24 * 60 * 60 * 1000;

export function isWithinAllowedProductSetEditingTimeframe(productSet: Pick<CompanyProductSet, 'createdAt'>) {
  const timestampToConsider = productSet.createdAt;
  const milliSecondsSinceCreation = Date.now() - new Date(timestampToConsider).getTime();
  if (milliSecondsSinceCreation > productSetEditingTimeFrameMs) return false;
  return true;
}

export function isBlanketProductSet(productSet: Pick<CompanyProductSet, 'blanketProductSetId'>) {
  return !isNil(productSet.blanketProductSetId);
}

function isResolvedSet(productSet: CompanyProductSet) {
  return (
    productSet.source.indexOf('resolver-v') > -1 &&
    productSet.source.indexOf('duplicated') === -1 &&
    productSet.source.indexOf('manual') === -1
  );
}
